import React, { forwardRef, useImperativeHandle } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Node } from '@tiptap/core';
import VariablePill from './VariablePill';

const VariableNode = Node.create({
    name: 'variablePill',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
        return {
            path: { default: null },
            display: { default: null },
            dataType: { default: 'string' },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-variable-pill]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', { 'data-variable-pill': '', ...HTMLAttributes }, 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(VariablePill);
    },
});

const PromptEditor = forwardRef(({ initialContent, onUpdate, onFocus }, ref) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                bold: false,
                italic: false,
                strike: false,
                code: false,
                codeBlock: false,
                blockquote: false,
                horizontalRule: false,
                bulletList: false,
                orderedList: false,
            }),
            VariableNode,
        ],
        content: initialContent || '',
        editorProps: {
            attributes: {
                // ⭐ THE FIX: Removed `prose` and `prose-invert`. Added explicit text color, size, and font.
                class: 'text-slate-200 text-sm font-mono leading-snug max-w-none py-3 pr-3 pl-14 focus:outline-none bg-slate-900 min-h-[120px] rounded-md',
            },
        },
        onUpdate: ({ editor }) => {
            onUpdate(editor.getJSON());
        },
        onCreate: ({ editor }) => {
            editor.on('focus', () => {
                if (onFocus) onFocus(editor);
            });
        },
    });

    useImperativeHandle(ref, () => editor);

    if (!editor) {
        return null;
    }

    return (
        <div className="prompt-editor-container relative border border-slate-700 rounded-md focus-within:ring-2 focus-within:ring-indigo-500">
            <EditorContent editor={editor} />
            
            <style jsx global>{`
                .prompt-editor-container .ProseMirror {
                    counter-reset: line;
                }

                /* This rule prevents an extra line number on an empty editor */
                .prompt-editor-container .ProseMirror p.is-editor-empty:first-child::before {
                    content: '';
                }

                .prompt-editor-container .ProseMirror p {
                    position: relative;
                    counter-increment: line;
                }

                .prompt-editor-container .ProseMirror p::before {
                    content: counter(line);
                    position: absolute;
                    left: -2.5rem;
                    width: 2rem;
                    text-align: right;
                    color: #64748B; /* slate-500 */
                }
            `}</style>
        </div>
    );
});

export default PromptEditor;